/**
 * useMergedCategories Hook
 * Merges system category templates with organization-specific custom categories
 * Used by LeftToolbar to display unified category list
 */

import { useQuery } from '@tanstack/react-query';
import { useOrganization } from '@clerk/clerk-react';
import { categoryTemplateService } from '@/services/categoryTemplateService';
import {
  organizationCategoryService,
  type DomainType,
} from '../services/organizationCategoryService';
import { logger } from '@/lib/logger';

export interface MergedCategory {
  id: string;
  domain: DomainType;
  category: string;
  subcategory: string | null;
  display_name: string;

  // Icon configuration (unified format)
  icon_type: 'svg' | 'abbreviation';
  icon_svg?: string | null;
  abbreviation?: string;
  icon_shape?: 'square' | 'circle';
  icon_width?: number;
  icon_height?: number;
  fill_color: string;
  border_color?: string;
  text_color?: string;

  // Metadata
  display_order: number;
  is_system: boolean; // true = from category_templates, false = from org_categories
  is_active: boolean;
  org_category_id?: string; // Reference to organization_categories.id if custom

  // Original data reference (for compatibility)
  original?: any;
}

/**
 * Generate abbreviation from category name
 */
function generateAbbreviation(name: string): string {
  const words = name
    .toUpperCase()
    .trim()
    .split(/[\s_-]+/);
  if (words.length === 1) {
    return words[0].substring(0, 3);
  }
  return words
    .slice(0, 4)
    .map(w => w[0])
    .join('');
}

/**
 * Map system template to MergedCategory format
 */
function mapSystemTemplateToMerged(template: any): MergedCategory {
  return {
    id: template.id,
    domain: template.domain as DomainType,
    category: template.category,
    subcategory: template.subcategory || null,
    display_name: template.display_name || template.category,
    icon_type: template.icon_svg ? 'svg' : 'abbreviation',
    icon_svg: template.icon_svg || null,
    abbreviation: generateAbbreviation(template.category),
    icon_shape: 'square',
    fill_color: template.default_color || '#6B7280',
    border_color: '#374151',
    text_color: '#FFFFFF',
    display_order: template.display_order || 0,
    is_system: true,
    is_active: template.is_active !== false,
    original: template,
  };
}

/**
 * Map organization category to MergedCategory format
 */
function mapOrgCategoryToMerged(oc: any): MergedCategory {
  return {
    id: oc.id,
    domain: oc.domain as DomainType,
    category: oc.category,
    subcategory: oc.subcategory || null,
    display_name: oc.display_name,
    icon_type: oc.icon_type,
    icon_svg: oc.icon_svg,
    abbreviation: oc.abbreviation,
    icon_shape: oc.icon_shape,
    icon_width: oc.icon_width,
    icon_height: oc.icon_height,
    fill_color: oc.fill_color,
    border_color: oc.border_color,
    text_color: oc.text_color,
    display_order: oc.display_order ?? 999, // Custom categories at end by default
    is_system: false,
    is_active: oc.is_active,
    org_category_id: oc.id,
    original: oc,
  };
}

/**
 * Hook to get merged categories (system + org) for a domain
 */
export function useMergedCategories(domain: DomainType) {
  const { organization, isLoaded } = useOrganization();
  const orgId = organization?.id;

  return useQuery({
    queryKey: ['merged-categories', domain, orgId],
    queryFn: async (): Promise<MergedCategory[]> => {
      try {
        // 1. Fetch system templates
        const systemTemplates = await categoryTemplateService.getByDomain(domain);

        // 2. Fetch org categories (if authenticated and org selected)
        let orgCategories: any[] = [];
        if (orgId) {
          try {
            orgCategories = await organizationCategoryService.getCategories(domain);
          } catch (error) {
            // Org categories may not exist yet or user not authenticated
            logger.debug('Could not fetch org categories:', error);
          }
        }

        // 3. Create lookup for org categories by key
        const orgCategoryMap = new Map<string, any>();
        orgCategories.forEach(oc => {
          const key = `${oc.category}:${oc.subcategory || ''}`;
          orgCategoryMap.set(key, oc);
        });

        // 4. Merge: system templates + org overrides + org-only categories
        const merged: MergedCategory[] = [];
        const processedKeys = new Set<string>();

        // Process system templates (org can override)
        for (const template of systemTemplates) {
          const key = `${template.category}:${template.subcategory || ''}`;
          const orgOverride = orgCategoryMap.get(key);

          if (orgOverride) {
            // Org overrides system template
            merged.push(mapOrgCategoryToMerged(orgOverride));
          } else {
            // Use system template as-is
            merged.push(mapSystemTemplateToMerged(template));
          }
          processedKeys.add(key);
        }

        // Add org-only categories (not overriding system)
        for (const oc of orgCategories) {
          const key = `${oc.category}:${oc.subcategory || ''}`;
          if (!processedKeys.has(key)) {
            merged.push(mapOrgCategoryToMerged(oc));
          }
        }

        // Sort by display_order, then alphabetically by display_name
        return merged.sort(
          (a, b) =>
            a.display_order - b.display_order || a.display_name.localeCompare(b.display_name),
        );
      } catch (error) {
        logger.error('Error fetching merged categories:', error);
        throw error;
      }
    },
    enabled: isLoaded,
    staleTime: 30000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get merged categories organized as parent-child hierarchy
 */
export function useMergedCategoriesHierarchy(domain: DomainType) {
  const { data: categories, ...rest } = useMergedCategories(domain);

  const hierarchy = categories
    ? {
        parents: categories.filter(c => !c.subcategory),
        children: categories.reduce((acc, cat) => {
          if (cat.subcategory) {
            const parentKey = cat.category;
            if (!acc.has(parentKey)) {
              acc.set(parentKey, []);
            }
            acc.get(parentKey)!.push(cat);
          }
          return acc;
        }, new Map<string, MergedCategory[]>()),
      }
    : { parents: [], children: new Map<string, MergedCategory[]>() };

  return { ...rest, data: categories, hierarchy };
}

export type { DomainType };
